import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Clock, 
  User, 
  MessageSquare, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  History
} from 'lucide-react';
import pqrsService from '@/services/pqrs.service';

const PqrDetailModal = ({ isOpen, onClose, pqr, onUpdate }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [comentario, setComentario] = useState('');
  const [accion, setAccion] = useState('');
  const [nuevoEstado, setNuevoEstado] = useState(pqr?.estado || 'Abierta');
  const [nuevaPrioridad, setNuevaPrioridad] = useState(pqr?.prioridad || 'Media');
  const [solucion, setSolucion] = useState('');
  const [validada, setValidada] = useState(false);
  const [comentarioValidacion, setComentarioValidacion] = useState('');

  const handleUpdateEstado = async () => {
    if (nuevoEstado === pqr.estado) {
      toast({
        title: "Error",
        description: "Debe seleccionar un estado diferente",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await pqrsService.updatePqr(pqr._id, {
        estado: nuevoEstado,
        usuarioActualizacion: {
          id: user._id,
          nombre: user.nombre,
          correo: user.correo
        }
      });

      if (response.success) {
        toast({
          title: "Éxito",
          description: "Estado actualizado exitosamente",
        });
        onUpdate();
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast({
        title: "Error",
        description: "Error al actualizar el estado",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrioridad = async () => {
    if (nuevaPrioridad === pqr.prioridad) {
      toast({
        title: "Error",
        description: "Debe seleccionar una prioridad diferente",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await pqrsService.updatePqr(pqr._id, {
        prioridad: nuevaPrioridad,
        usuarioActualizacion: {
          id: user._id,
          nombre: user.nombre,
          correo: user.correo
        }
      });

      if (response.success) {
        toast({
          title: "Éxito",
          description: "Prioridad actualizada exitosamente",
        });
        onUpdate();
      }
    } catch (error) {
      console.error('Error al actualizar prioridad:', error);
      toast({
        title: "Error",
        description: "Error al actualizar la prioridad",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComentario = async () => {
    if (!comentario.trim()) {
      toast({
        title: "Error",
        description: "Debe ingresar un comentario",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await pqrsService.updatePqr(pqr._id, {
        comentario: {
          comentario: comentario.trim(),
          accion: accion || undefined
        },
        usuarioActualizacion: {
          id: user._id,
          nombre: user.nombre,
          correo: user.correo
        }
      });

      if (response.success) {
        toast({
          title: "Éxito",
          description: "Comentario agregado exitosamente",
        });
        setComentario('');
        setAccion('');
        setShowCommentForm(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error al agregar comentario:', error);
      toast({
        title: "Error",
        description: "Error al agregar el comentario",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCerrarPqr = async () => {
    if (!solucion.trim()) {
      toast({
        title: "Error",
        description: "Debe ingresar la solución aplicada",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await pqrsService.cerrarPqr(pqr._id, {
        solucion: solucion.trim(),
        usuarioCierre: {
          id: user._id,
          nombre: user.nombre,
          correo: user.correo
        },
        validada: validada,
        comentarioValidacion: comentarioValidacion || undefined
      });

      if (response.success) {
        toast({
          title: "Éxito",
          description: "PQR cerrada exitosamente",
        });
        setSolucion('');
        setValidada(false);
        setComentarioValidacion('');
        setShowCloseForm(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error al cerrar PQR:', error);
      toast({
        title: "Error",
        description: error.response?.data?.message || 'Error al cerrar la PQR',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'Abierta': 'bg-red-100 text-red-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      'En espera': 'bg-yellow-100 text-yellow-800',
      'Cerrada': 'bg-green-100 text-green-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      'Alta': 'bg-red-100 text-red-800',
      'Media': 'bg-yellow-100 text-yellow-800',
      'Baja': 'bg-green-100 text-green-800'
    };
    return colors[prioridad] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'Petición': 'bg-blue-100 text-blue-800',
      'Queja': 'bg-yellow-100 text-yellow-800',
      'Reclamo': 'bg-red-100 text-red-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  if (!pqr) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Detalle de PQR - {pqr.consecutivo}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-500">Tipo</Label>
                <div className="mt-1">
                  <Badge className={getTipoColor(pqr.tipo)}>{pqr.tipo}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Categoría</Label>
                <p className="mt-1 font-medium">{pqr.categoria}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Estado</Label>
                <div className="mt-1">
                  <Badge className={getEstadoColor(pqr.estado)}>{pqr.estado}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Prioridad</Label>
                <div className="mt-1">
                  <Badge className={getPrioridadColor(pqr.prioridad)}>{pqr.prioridad}</Badge>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="text-sm text-gray-500">Descripción</Label>
              <p className="mt-1 text-sm whitespace-pre-wrap">{pqr.descripcion}</p>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-500 flex items-center gap-1">
                  <User className="h-4 w-4" />
                  Usuario que Reporta
                </Label>
                <p className="mt-1 font-medium">{pqr.usuarioReporta?.nombre || '-'}</p>
                {pqr.usuarioReporta?.correo && (
                  <p className="text-sm text-gray-500">{pqr.usuarioReporta.correo}</p>
                )}
              </div>
              <div>
                <Label className="text-sm text-gray-500 flex items-center gap-1">
                  <User className="h-4 w-4" />
                  Responsable
                </Label>
                <p className="mt-1 font-medium">{pqr.responsable?.nombre || 'Sin asignar'}</p>
                {pqr.responsable?.correo && (
                  <p className="text-sm text-gray-500">{pqr.responsable.correo}</p>
                )}
              </div>
              <div>
                <Label className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Fecha Registro
                </Label>
                <p className="mt-1 text-sm">
                  {pqr.fechaRegistro ? format(new Date(pqr.fechaRegistro), 'dd/MM/yyyy HH:mm', { locale: es }) : '-'}
                </p>
              </div>
              {pqr.fechaCierre && (
                <div>
                  <Label className="text-sm text-gray-500 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Fecha Cierre
                  </Label>
                  <p className="mt-1 text-sm">
                    {format(new Date(pqr.fechaCierre), 'dd/MM/yyyy HH:mm', { locale: es })}
                  </p>
                </div>
              )}
            </div>

            {pqr.estado !== 'Cerrada' && (
              <>
                <Separator />
                <div className="space-y-4">
                  <h3 className="font-semibold">Actualizar PQR</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Estado</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Abierta">Abierta</SelectItem>
                            <SelectItem value="En proceso">En proceso</SelectItem>
                            <SelectItem value="En espera">En espera</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={handleUpdateEstado} disabled={loading} size="sm">
                          Actualizar
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Prioridad</Label>
                      <div className="flex gap-2 mt-1">
                        <Select value={nuevaPrioridad} onValueChange={setNuevaPrioridad}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Alta">Alta</SelectItem>
                            <SelectItem value="Media">Media</SelectItem>
                            <SelectItem value="Baja">Baja</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={handleUpdatePrioridad} disabled={loading} size="sm">
                          Actualizar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {pqr.solucion && (
              <>
                <Separator />
                <div>
                  <Label className="text-sm text-gray-500 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Solución
                  </Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{pqr.solucion}</p>
                  {pqr.usuarioCierre && (
                    <p className="text-sm text-gray-500 mt-1">
                      Por: {pqr.usuarioCierre.nombre}
                    </p>
                  )}
                  {pqr.validacionSolucion && (
                    <div className="mt-2 p-2 bg-gray-50 rounded">
                      <p className="text-sm font-medium">
                        {pqr.validacionSolucion.validada ? '✓ Solución validada' : '✗ Solución no validada'}
                      </p>
                      {pqr.validacionSolucion.comentario && (
                        <p className="text-sm text-gray-600 mt-1">{pqr.validacionSolucion.comentario}</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {pqr.comentarios && pqr.comentarios.length > 0 && (
              <>
                <Separator />
                <div>
                  <Label className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                    <MessageSquare className="h-4 w-4" />
                    Comentarios ({pqr.comentarios.length})
                  </Label>
                  <div className="space-y-3">
                    {pqr.comentarios.map((com, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-medium text-sm">{com.usuario?.nombre}</p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(com.fecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700">{com.comentario}</p>
                        {com.accion && (
                          <p className="text-xs text-blue-600 mt-1">Acción: {com.accion}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {pqr.historialCambios && pqr.historialCambios.length > 0 && (
              <>
                <Separator />
                <div>
                  <Label className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                    <History className="h-4 w-4" />
                    Historial de Cambios
                  </Label>
                  <div className="space-y-2">
                    {pqr.historialCambios.map((cambio, index) => (
                      <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                        <p>
                          <span className="font-medium">{cambio.usuario?.nombre}</span> cambió el estado de{' '}
                          <Badge className={getEstadoColor(cambio.estadoAnterior)} variant="outline">
                            {cambio.estadoAnterior}
                          </Badge>
                          {' a '}
                          <Badge className={getEstadoColor(cambio.estadoNuevo)} variant="outline">
                            {cambio.estadoNuevo}
                          </Badge>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(new Date(cambio.fecha), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </p>
                        {cambio.comentario && (
                          <p className="text-xs text-gray-600 mt-1">{cambio.comentario}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {pqr.estado !== 'Cerrada' && (
              <>
                <Separator />
                <div className="space-y-4">
                  {!showCommentForm && !showCloseForm && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowCommentForm(true)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Agregar Comentario
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => setShowCloseForm(true)}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Cerrar PQR
                      </Button>
                    </div>
                  )}

                  {showCommentForm && (
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">Agregar Comentario</h4>
                      <div>
                        <Label>Comentario *</Label>
                        <Textarea
                          value={comentario}
                          onChange={(e) => setComentario(e.target.value)}
                          placeholder="Ingrese su comentario..."
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Acción Realizada (Opcional)</Label>
                        <Textarea
                          value={accion}
                          onChange={(e) => setAccion(e.target.value)}
                          placeholder="Describa la acción realizada..."
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleAddComentario} disabled={loading}>
                          Guardar Comentario
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowCommentForm(false);
                            setComentario('');
                            setAccion('');
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}

                  {showCloseForm && (
                    <div className="space-y-3 p-4 border rounded-lg">
                      <h4 className="font-medium">Cerrar PQR</h4>
                      <div>
                        <Label>Solución Aplicada *</Label>
                        <Textarea
                          value={solucion}
                          onChange={(e) => setSolucion(e.target.value)}
                          placeholder="Describa la solución aplicada..."
                          rows={4}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="validada"
                          checked={validada}
                          onChange={(e) => setValidada(e.target.checked)}
                          className="h-4 w-4"
                        />
                        <Label htmlFor="validada">Solución validada</Label>
                      </div>
                      {validada && (
                        <div>
                          <Label>Comentario de Validación</Label>
                          <Textarea
                            value={comentarioValidacion}
                            onChange={(e) => setComentarioValidacion(e.target.value)}
                            placeholder="Comentario sobre la validación..."
                            rows={2}
                          />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button onClick={handleCerrarPqr} disabled={loading}>
                          Cerrar PQR
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowCloseForm(false);
                            setSolucion('');
                            setValidada(false);
                            setComentarioValidacion('');
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PqrDetailModal;
